import React, { useCallback, useEffect, useState } from 'react';
import { ServerInterface } from "src/store/mine_craft/serversSlice";
import { banServerPlayer, fetchServerBans, unbanServerPlayer } from "src/actions/mine_craft/serversActions";
import { useAppDispatch } from "src/store/hooks";
import usePermissions from "src/hooks/usePermissions";
import { formattedDate } from "src/helpers/DateHelpers";

import { Button, Input, message, Popconfirm, Tag } from 'antd';
import { InboxOutlined, StopOutlined, SyncOutlined } from '@ant-design/icons';
import Card from "src/componets/Card";
import { shortFromNow } from "./_serverHelpers";

interface Ban {
  player: string;
  xuid: string | null;
  reason: string | null;
  createdAt: string;
}

// Mirrors User::GAMERTAG_REGEX in the minecraft app.
const GAMERTAG_REGEX = /^[A-Za-z0-9 _.\-]{1,32}$/;
const MAX_REASON = 100;

// Bans are kept by crafty-servers itself (vanilla Bedrock has no ban
// list) and enforced by auto-kick on every connect — an online player is
// kicked immediately, with the reason in the kick message.
const _Bans = ({ server, onSelectPlayer }: {
  server: ServerInterface,
  onSelectPlayer?: (gamertag: string) => void
}) => {
  const dispatch = useAppDispatch();
  const { write_minecraft } = usePermissions();

  const [bans, setBans] = useState<Ban[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [gamertag, setGamertag] = useState('');
  const [reason, setReason] = useState('');

  const serverId = server.id;

  const loadBans = useCallback(async () => {
    setLoading(true);
    const resultAction = await dispatch(fetchServerBans(serverId));
    setLoading(false);

    if (fetchServerBans.fulfilled.match(resultAction)) {
      setBans(resultAction.payload.bans || []);
    }
  }, [serverId, dispatch]);

  useEffect(() => {
    loadBans().catch(console.error);
  }, [loadBans]);

  function fail(payload: any, fallback: string) {
    message.error(Array.isArray(payload) ? payload[0] : fallback);
  }

  async function ban() {
    const tag = gamertag.trim();
    if (!GAMERTAG_REGEX.test(tag)) return;

    setBusy(true);
    const resultAction = await dispatch(banServerPlayer({
      id:       serverId,
      gamertag: tag,
      reason:   reason.trim() || undefined
    }));
    setBusy(false);

    if (banServerPlayer.fulfilled.match(resultAction)) {
      message.success(`${tag} banned`);
      setGamertag('');
      setReason('');
      loadBans().catch(console.error);
    } else {
      fail(resultAction.payload, 'Ban failed');
    }
  }

  async function unban(tag: string) {
    const resultAction = await dispatch(unbanServerPlayer({ id: serverId, gamertag: tag }));

    if (unbanServerPlayer.fulfilled.match(resultAction)) {
      message.success(`${tag} unbanned`);
      loadBans().catch(console.error);
    } else {
      fail(resultAction.payload, 'Unban failed');
    }
  }

  return (
    <Card className='v-srv-bans'>
      <div className='o-head'>
        <h4 className='v-content-title'>Bans</h4>
        <span className='o-count'>{bans.length}</span>
        <Button
          className='o-icon-btn'
          icon={<SyncOutlined />}
          title='Refresh'
          aria-label='Refresh bans'
          loading={loading}
          onClick={() => loadBans()}
        />
      </div>

      {write_minecraft &&
        <div className='o-form'>
          <Input
            className='o-tag-input'
            placeholder='Gamertag'
            maxLength={32}
            value={gamertag}
            disabled={busy}
            onChange={e => setGamertag(e.target.value)}
          />
          <Input
            className='o-reason-input'
            placeholder='Reason (shown in the kick message)'
            maxLength={MAX_REASON}
            value={reason}
            disabled={busy}
            onChange={e => setReason(e.target.value)}
            onPressEnter={() => ban()}
          />
          <Popconfirm
            title={`Ban ${gamertag.trim() || 'this player'}? They are kicked now and on every reconnect.`}
            onConfirm={ban}
            disabled={busy || !GAMERTAG_REGEX.test(gamertag.trim())}
          >
            <Button
              danger
              icon={<StopOutlined />}
              disabled={busy || !GAMERTAG_REGEX.test(gamertag.trim())}
            >
              Ban
            </Button>
          </Popconfirm>
        </div>
      }

      <div className='o-list'>
        {bans.map(entry => (
          <div key={entry.player} className='v-srv-member'>
            <div className='o-who'>
              <span className='o-avatar o-empty' />
              <div className='o-names'>
                <div
                  className={`o-tag ${onSelectPlayer ? 'o-clickable' : ''}`}
                  onClick={() => onSelectPlayer?.(entry.player)}
                >
                  {entry.player}
                </div>
                <div className='o-sub'>
                  {entry.reason && <span>{entry.reason}</span>}
                  {entry.createdAt &&
                    <span title={formattedDate(entry.createdAt).dateTime}>
                      banned {shortFromNow(entry.createdAt)}
                    </span>}
                </div>
              </div>
            </div>

            <Tag color='red'>banned</Tag>

            {write_minecraft &&
              <div className='o-actions'>
                <Popconfirm
                  title={`Unban ${entry.player}? They can reconnect immediately.`}
                  onConfirm={() => unban(entry.player)}
                >
                  <Button size='small'>Unban</Button>
                </Popconfirm>
              </div>
            }
          </div>
        ))}
      </div>

      {!loading && !bans.length &&
        <div className='v-srv-empty'>
          <InboxOutlined className='o-icon' />
          <div className='o-title'>Nobody is banned</div>
          <div className='o-hint'>
            {write_minecraft
              ? 'Ban a gamertag above to keep a player off the server.'
              : 'Banned players would show up here.'}
          </div>
        </div>
      }
    </Card>
  );
}

export default _Bans;
